import { useState } from "react";
import { Button, EmptyState, Spinner } from "@cntrl/ui";
import type { components } from "@cntrl/api-client";
import { RequireBand } from "../../app/RequireBand.js";
import { useShows } from "../../app/hooks/useShows.js";
import { useSharedShows } from "../../app/hooks/useShowShares.js";
import { upcomingSharedShows } from "../booking/lib/bookingRows.js";
import { useOnlineStatus } from "../../app/hooks/useOnlineStatus.js";
import { ApiError } from "../../app/hooks/apiError.js";
import { DayOfShowDetail } from "./DayOfShowDetail.js";
import { useMobileBack } from "../../app/mobileBack.js";
import { OfflineBanner } from "./OfflineBanner.js";
import { BusSheetButton } from "./BusSheetButton.js";
import { isoDateOffset, partitionByToday, locationLine, listCardDate } from "./dayOfShow.js";
import styles from "./dayOfShow.module.scss";

type ShowSummary = components["schemas"]["ShowSummary"];

/**
 * The `/dos` center-tab surface — resolves TODAY's show and lands on its day-of-show, or shows
 * the designed LIST MODE when there is no single show today (the born-blank empty state, never a blank tab).
 * "Today" uses the confirmed-shows list filtered `from = today` (BookingScreen's 6am-next-day cutoff — a show ON
 * today stays current until tomorrow); exactly one today → straight to its detail, otherwise the picker list.
 */
export interface DayOfShowScreenProps {
  /** Route to the mobile door check-in for a show (essentials "Guest List"). */
  onOpenGuests: (showId: string) => void;
  /** Route to the show's VIP list (essentials "VIP"). */
  onOpenVip: (showId: string) => void;
  /** Route to the show's logistics page (essentials "Logistics" + flights "Live status"). */
  onOpenLogistics: (showId: string) => void;
  /** The show to open on mount, from the `/dos?show=` param — so returning from a per-show sub-screen
   *  (VIP / door / logistics) lands back on the show that was open, not the auto/list resolution. */
  initialShowId?: string;
  /** Reflect the open show (or null for the list) into the URL, so a round-trip through a sub-screen restores
   *  it. Router-owned by the route wrapper; kept a prop so this component stays render-context free (tested
   *  via renderWithBand, which mounts no RouterProvider). */
  onShowChange?: (showId: string | null) => void;
  /**
   * The show was opened by a LINK from another screen (a chat's Day of Show link, `?via=link`), and this returns
   * there — a history-back. While that show is open, its back leaves the tab instead of showing the list, and it
   * is registered as the mobile back so the iOS edge swipe agrees (ART-268). Absent when the show was picked here.
   */
  onLeave?: () => void;
}

export function DayOfShowScreen(props: DayOfShowScreenProps) {
  return (
    <RequireBand>
      <DayOfShowTabInner {...props} />
    </RequireBand>
  );
}

function DayOfShowTabInner({
  onOpenGuests,
  onOpenVip,
  onOpenLogistics,
  initialShowId,
  onShowChange,
  onLeave,
}: DayOfShowScreenProps) {
  const online = useOnlineStatus();
  const todayIso = isoDateOffset(0);
  // Confirmed shows from today forward, soonest first — the DOS tab only ever runs a real day-of on a confirmed
  // show (holds/offers aren't day-of-show material). The window is unbounded forward so list mode can offer the
  // next upcoming show when nothing is today.
  const shows = useShows({ status: "confirmed", from: todayIso, sort: "date" });
  // Shows this band PLAYS as a guest (an accepted support-slot share, ART-249) live on the host band's spine,
  // so the own-band list never carries them. Fold in the accepted ones whose HOST date is confirmed, matching
  // the confirmed-only rule above. Fail-soft: a shared-shows error must not take the band's own shows down.
  const shared = useSharedShows();
  // View mode: "auto" = auto-open the single show today (else list); "list" = the user asked for the list
  // (Back / browse); a showId = an explicit pick. Keeping "list" distinct from "auto" is what makes Back work
  // when exactly one show is today — otherwise Back would re-derive the same auto-open (adversarial review).
  // Seeded from the URL so a return from VIP/door/logistics reopens the show that was picked (ART-153 pass two).
  const [view, setView] = useState<"auto" | "list" | string>(initialShowId ?? "auto");
  // A pick both opens the show AND reflects it in the URL, so history-back from a sub-screen restores it.
  const pick = (id: string) => {
    setView(id);
    onShowChange?.(id);
  };
  // The linked show is still the one open: back returns to the screen that linked here, and the swipe with it.
  const leave = onLeave && initialShowId && view === initialShowId ? onLeave : null;
  useMobileBack(leave);

  if (shows.isLoading) {
    return (
      <div className={styles.center} role="status" aria-live="polite">
        <Spinner size={22} label="Loading your shows" />
      </div>
    );
  }
  if (shows.isError) {
    return (
      <div className={styles.screen}>
        {!online && <OfflineBanner />}
        <EmptyState
          title="Couldn't load your shows"
          description={shows.error instanceof ApiError ? shows.error.message : "Check your connection and try again."}
          action={<Button onClick={() => void shows.refetch()}>Retry</Button>}
        />
      </div>
    );
  }

  const sharedItems = upcomingSharedShows(shared.data, todayIso);
  const items = [...(shows.data?.items ?? []), ...sharedItems]
    .sort((a, b) => (a.date ?? "").localeCompare(b.date ?? ""));
  const { today, upcoming } = partitionByToday(items, todayIso);

  // "list" forces the picker; an explicit pick (showId) wins; "auto" auto-opens the single show today.
  const activeId =
    view === "list" ? null : view !== "auto" ? view : today.length === 1 ? today[0]!.showId! : null;
  if (activeId) {
    // The DOS detail always carries the in-body back arrow (Ryan, ART-174) — the shell top bar is killed on
    // `/dos`, so this is the only back. It forces the "list" view (overriding the single-show-today auto-open),
    // which shows the picker even when there is one show.
    return (
      <DayOfShowDetail
        showId={activeId}
        onBack={leave ?? (() => { setView("list"); onShowChange?.(null); })}
        onOpenGuests={onOpenGuests}
        onOpenVip={onOpenVip}
        onOpenLogistics={onOpenLogistics}
      />
    );
  }

  // ---- List mode (no single show today) -----------------------------------------------------------------
  // No confirmed shows from today forward → just "No Shows Scheduled", centred (Ryan, ART-174).
  if (items.length === 0) {
    return (
      <div className={styles.list} data-testid="dos-list">
        {!online && <OfflineBanner />}
        <div className={styles.listTools}><BusSheetButton /></div>
        <div className={styles.emptyCenter}>
          <div className={styles.emptyTitle}>No shows scheduled</div>
        </div>
      </div>
    );
  }
  return (
    <div className={styles.list} data-testid="dos-list">
      {!online && <OfflineBanner />}
      {/* The bus sheet is reachable with no show open (ART-148): a bunk is per tour leg, not per date. */}
      <div className={styles.listTools}><BusSheetButton /></div>
      {today.length > 0 && (
        <ShowGroup shows={today} todayIso={todayIso} onPick={pick} onLogistics={onOpenLogistics} />
      )}
      {upcoming.length > 0 && (
        <ShowGroup shows={upcoming.slice(0, 20)} todayIso={todayIso} onPick={pick} onLogistics={onOpenLogistics} />
      )}
    </div>
  );
}

function ShowGroup({
  shows,
  todayIso,
  onPick,
  onLogistics,
}: {
  shows: readonly ShowSummary[];
  todayIso: string;
  onPick: (id: string) => void;
  onLogistics: (id: string) => void;
}) {
  return (
    <ul className={styles.showList}>
      {shows.map((s) => {
        const isToday = s.date === todayIso;
        const city = locationLine({ city: s.city, state: s.state });
        return (
          <li key={s.showId}>
            {/* A div role=button, not a <button>: a real button centres block children and can't legally
                nest the "Logistics" action. */}
            <div
              role="button"
              tabIndex={0}
              aria-label={s.venueName || "Show"}
              className={styles.listcard}
              data-today={isToday || undefined}
              onClick={() => s.showId && onPick(s.showId)}
              onKeyDown={(e) => {
                if ((e.key === "Enter" || e.key === " ") && s.showId) {
                  e.preventDefault();
                  onPick(s.showId);
                }
              }}
            >
              <div className={styles.listcardHead}>
                <span className={styles.listcardDate}>{listCardDate(s.date, todayIso)}</span>
                <span className={styles.listcardPill} data-today={isToday || undefined}>
                  {isToday ? "Day of" : "Confirmed"}
                </span>
              </div>
              <div className={styles.listcardVenue}>{s.venueName || "Show"}</div>
              {city && <div className={styles.listcardCity}>{city}</div>}
              <div className={styles.listcardActions}>
                {/* The two actions must not both fire — stop the card's tap on the Logistics button. */}
                <button
                  type="button"
                  className={styles.listcardAction}
                  data-variant="logistics"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (s.showId) onLogistics(s.showId);
                  }}
                >
                  Logistics →
                </button>
                <span className={styles.listcardAction}>Open →</span>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
